import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/Button';

/**
 * UnsavedChangesIndicator Component
 *
 * Shows a badge indicating unsaved changes
 */
function UnsavedChangesIndicator({ changeTypes, t }) {
  if (!changeTypes || changeTypes.length === 0) return null;

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      {t('modal.unsavedChanges', { defaultValue: 'Unsaved changes' })}
    </span>
  );
}

/**
 * ChangeTypeLabels Component
 *
 * Shows labels for each type of change
 */
function ChangeTypeLabels({ changeTypes, t }) {
  if (!changeTypes || changeTypes.length === 0) return null;

  const labelMap = {
    style: t('modal.changes.style', { defaultValue: 'Style' }),
    caption: t('modal.changes.caption', { defaultValue: 'Caption' }),
    position: t('modal.changes.position', { defaultValue: 'Position' }),
    alignment: t('modal.changes.alignment', { defaultValue: 'Alignment' }),
    background: t('modal.changes.background', { defaultValue: 'Background' }),
    gradient: t('modal.changes.gradient', { defaultValue: 'Gradient' }),
    font: t('modal.changes.font', { defaultValue: 'Font' }),
    gif: t('modal.changes.gif', { defaultValue: 'GIF' }),
  };

  const labels = changeTypes.map((type) => labelMap[type] || type).filter(Boolean);

  return labels.length > 0 ? (
    <span className="ml-1">({labels.join(', ')})</span>
  ) : null;
}

/**
 * ModalActionFooter Component
 *
 * Reusable footer for approval modals with:
 * - Change indicator (left side)
 * - Cancel and Primary action buttons (right side)
 *
 * @param {Object} props
 * @param {boolean} props.hasChanges - Whether there are unsaved changes
 * @param {string[]} props.changeTypes - Array of change type identifiers
 * @param {boolean} props.isProcessing - Whether primary action is in progress
 * @param {boolean} props.disabled - Whether primary button is disabled
 * @param {Function} props.onCancel - Cancel button click handler
 * @param {Function} props.onPrimaryAction - Primary button click handler
 * @param {string} props.primaryLabel - Label for primary button
 * @param {string} props.processingLabel - Label while processing
 * @param {boolean} props.showChangeLabels - Show change type labels on button
 * @param {string} props.cancelLabel - Custom cancel button label
 */
export function ModalActionFooter({
  hasChanges = false,
  changeTypes = [],
  isProcessing = false,
  disabled = false,
  onCancel,
  onPrimaryAction,
  primaryLabel,
  processingLabel,
  showChangeLabels = false,
  cancelLabel,
}) {
  const { t } = useTranslation('approval');

  return (
    <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 shadow-lg">
      <div className="flex justify-between items-center gap-3">
        {/* Left side - Change indicator */}
        <div className="flex items-center gap-2">
          {hasChanges && (
            <UnsavedChangesIndicator changeTypes={changeTypes} t={t} />
          )}
        </div>

        {/* Right side - Action buttons */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isProcessing}
          >
            {cancelLabel || t('modal.button.cancel', { defaultValue: 'Cancel' })}
          </Button>

          <Button
            variant="primary"
            onClick={onPrimaryAction}
            loading={isProcessing}
            disabled={disabled}
            className="min-w-[180px]"
          >
            {isProcessing ? processingLabel : primaryLabel}
            {!isProcessing && showChangeLabels && hasChanges && (
              <ChangeTypeLabels changeTypes={changeTypes} t={t} />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

ModalActionFooter.propTypes = {
  hasChanges: PropTypes.bool,
  changeTypes: PropTypes.arrayOf(PropTypes.string),
  isProcessing: PropTypes.bool,
  disabled: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onPrimaryAction: PropTypes.func.isRequired,
  primaryLabel: PropTypes.string.isRequired,
  processingLabel: PropTypes.string.isRequired,
  showChangeLabels: PropTypes.bool,
  cancelLabel: PropTypes.string,
};

export default ModalActionFooter;
